import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { AuthStatus, CookieOptions, LanguageRegionMap, NewUser, User } from 'src/app/types';
import { environment } from 'src/environments/environment';
import { ClientService } from '../client/client.service';
import { LanguageService } from '../language/language.service';
import { NotificationService } from '../notification/notification.service';
import { StorageService } from '../storage/storage.service';
import { TranslateService } from '../translation/translate.service';
import { firstValueFrom, Observable } from 'rxjs';
import { WINDOW } from '../window/window.service';
import { languageRegionCodesMap } from '../../constants';
import { SharedService } from '../shared-service/shared.service';
import { jwtDecode } from "jwt-decode";
import { HttpClient } from '@angular/common/http';

export interface AuthError {
  code: string;
  message: string;
}

export interface UserProfile {
  accessibleClients: AccessibleClient[];
  activeClient: ActiveClient;
  firstName: string;
  id: string;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  phoneNumber: string;
  lastName: string;
  permissions: Permission[];
  preferredLanguage: string;
  profilePictureUrl: string | null;
  username: string;
}

export interface AccessibleClient {
  id: string;
  logoUrl: string | null;
  name: string;
}

export interface ActiveClient {
  alternativeCurrencies: string[];
  clientType: ClientType;
  defaultCurrency: string;
  id: string;
  languages: string[];
  logoUrl: string | null;
  name: string;
  shortName: string;
  theme: Theme;
  vatPercentage: number;
}

export interface Theme {
  onPrimaryColor: string;
  onSecondaryColor: string;
  primaryColor: string;
  secondaryColor: string;
}

export type ClientType = "FREEMIUM";

export type Permission =
  | "ASSIGN_TASKS"
  | "APPROVE_INSPECTION"
  | "RUN_AUCTIONS"
  | "EDIT_STOCK_OPERATIONAL"
  | "EDIT_STOCK_FINANCIAL"
  | "CUSTOMER_SUPPORT_ACCESS"
  | "CREATE_AND_PUBLISH_LISTINGS"
  | "VIEW_STOCK_OPERATIONAL"
  | "APPROVE_VALUATION"
  | "VIEW_STOCK_BASIC"
  | "MARK_ITEMS_SOLD"
  | "FINANCIALS_ADMIN"
  | "APPROVE_ACCESS_REQUESTS"
  | "VIEW_FINANCIAL_OVERVIEW"
  | "MARKETING_ADMIN"
  | "VIEW_TEAM_PERFORMANCE"
  | "ARCHIVE_ITEMS"
  | "ACCOUNT_ADMIN"
  | "DELETE_CRM_CONTACTS"
  | "APPROVE_SELLING_AGREEMENT"
  | "APPROVE_PURCHASE_AGREEMENT"
  | "EDIT_STOCK_BASIC"
  | "VIEW_STOCK_FINANCIAL";


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public tmpPassword?: string;
  public currentUser: CognitoUser | null = null; // Store the authenticated user
  private sessionRefreshInterval: any = null; // Store the interval ID
  public cognitoUserPool = new CognitoUserPool({
    UserPoolId: environment.aws.cognitoPool,
    ClientId: environment.aws.cognitoClient,
  });

  public userData:UserProfile | undefined = undefined;
  public authData = signal<AuthStatus | undefined>(undefined);
  public isLoggedIn = signal<boolean>(false);
  public user = signal<User | undefined>(undefined);
  public verification = signal<'initial' | 'email' | 'phone' | 'complete'>(
    'initial'
  );
  private isBrowser: boolean = false;

  constructor(
    @Inject(WINDOW) public window: Window,
    @Inject(PLATFORM_ID) platformId: Object,
    private storageService: StorageService,
    private clientService: ClientService,
    private router: Router,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private langService: LanguageService,
    private shared: SharedService,
    private http: HttpClient,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.clientService.client?.isLoginEnabled) {
      console.log(
        '🔴 Client has not enabled login, not initializing auth service'
      );
      return;
    }

    if (this.getAccessToken()) {
      this.isLoggedIn.set(true);
    }

    this.checkVerification();
  }

  public getUserData() {
    const user = this.storageService.get('user');
    if (!user) {
      return null;
    }
    const userData = {
        Username: JSON.parse(user).email,
        Pool: this.cognitoUserPool,
    };
    return new CognitoUser(userData);
  }

  public checkVerification() {
    if (!this.authData()) {
      return;
    }
    const user = this.storageService.get('user');

    if (user) {
      const userData = {
        Username: JSON.parse(user).email,
        Pool: this.cognitoUserPool,
      };
      this.currentUser = new CognitoUser(userData);

      const _user = JSON.parse(user) as User;
      this.user.set(_user);

      if (_user.emailVerified && this.authData()?.hasCompletedSignup) {
        this.verification.set('complete');
      } else if (!_user.emailVerified) {
        this.verification.set('email');
      } else {
        this.verification.set('phone');
      }
    } else {
      this.verification.set('email');
    }
  }

  public forgotPasswordVerification(email: string): Promise<'success' | 'enter_code' | 'failure'> {

    return new Promise((resolve, reject) => {
      const userData = {
          Username: email,
          Pool: this.cognitoUserPool,
      };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.forgotPassword({
        onSuccess: function (result) {
          resolve('success');
        },
        onFailure: function (err) {
          resolve('failure');
        },
        inputVerificationCode: function () {
          resolve('enter_code');
        }
      });
    });
  }

  public changePassword(email: string, code: string, password: string): Promise<'success' | 'failure'> {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: this.cognitoUserPool,
      };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.confirmPassword(
        code,
        password,
        {
          onSuccess() {
            resolve('success');
          },
          onFailure(err) {
            resolve('failure');
          }
        }
      );
    });
  }

  public apiSignup = async (data: any): Promise<string | undefined> => {
    return new Promise((resolve, reject) => {
      this.http.post(`${environment.api}/public/v1/sign-up`, {
          ...data,
          phoneNumber: data.phone_number,
        }).subscribe({
        next: (response: any) => {
          resolve(response?.message);
        },
        error: (error) => {
          console.log('🔴 API signup failed:');
          console.log(error?.error?.message ?? 'An unknown error occurred');
          reject({
            code: error?.status ?? 'UnknownError',
            message: error?.error?.message ?? 'An unknown error occurred',
          } as AuthError);
        }
      });
    });
  }

  /* For cognito signup */
  public signUp = async (newUser: NewUser): Promise<string | undefined> => {
    return new Promise((resolve, reject) => {
      // Create user attributes array
      const attributeList: CognitoUserAttribute[] = [
        new CognitoUserAttribute({
          Name: 'phone_number',
          Value: newUser.phone_number,
        }),
      ];

      this.cognitoUserPool.signUp(
        newUser.email,
        newUser.password,
        attributeList, // Pass attributes here
        [],
        (err, data) => {
          if (err) {
            reject({
              // @ts-ignore
              code: err?.code ?? 'UnknownError',
              message: err?.message ?? 'An unknown error occurred',
            } as AuthError);
            return;
          }

          resolve(data?.user.getUsername()); // Resolve when signUp succeeds
        }
      );
    });
  };

  public confirmUser = async (
    email: string,
    verificationCode: string
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: this.cognitoUserPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
        if (err) {
          reject({
            code: err?.code ?? 'UnknownError',
            message: err?.message ?? 'An unknown error occurred',
          });
          return;
        }
        resolve();
      });
    });
  };

  public resendVerificationCode = async (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: this.cognitoUserPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject({
            // @ts-ignore
            code: err?.code ?? 'UnknownError',
            message: err?.message ?? 'An unknown error occurred',
          });
          return;
        }
        console.log('Verification code resent successfully:', result);
        resolve();
      });
    });
  };

  public requestPhoneVerificationCode = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        reject({
          code: 'NotAuthenticated',
          message: 'User is not authenticated.',
        });
        // TODO: log user out, snackbar error notification
        return;
      }

      this.currentUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          reject({
            code: err?.code ?? 'UnknownError',
            message:
              err?.message ?? 'An error occurred while getting the session.',
          });
          return;
        }
        // @ts-ignore
        this.currentUser.getAttributeVerificationCode('phone_number', {
          onSuccess: () => {
            console.log('✅ Phone verification code sent successfully.');
            resolve();
          },
          onFailure: (error) => {
            reject({
              // @ts-ignore
              code: error?.code ?? 'UnknownError',
              message:
                error?.message ?? 'Failed to send phone verification code.',
            });
          },
        });
      });
    });
  };

  public confirmPhoneVerificationCode = async (
    verificationCode: string
  ): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      if (!this.currentUser) {
        reject({
          code: 'NotAuthenticated',
          message: 'User is not authenticated.',
        });
        this.isLoggedIn.set(false);
        this.clearTokens();
        return;
      }

      // ✅ Try to refresh session before verifying phone
      try {
        await this.refreshSession();
      } catch (error) {
        reject(error);
        return;
      }

      this.currentUser.verifyAttribute('phone_number', verificationCode, {
        onSuccess: () => {
          console.log('✅ Phone number verified successfully.');

          // ✅ Update stored user data
          const storedUser = this.storageService.get('user');
          if (storedUser) {
            const user = JSON.parse(storedUser);
            user.phoneNumberVerified = true;
            this.storageService.set('user', JSON.stringify(user));
            this.user.set(user);
          }

          this.verification.set('complete');
          resolve();
        },
        onFailure: (error) => {
          reject({
            //@ts-ignore
            code: error?.code ?? 'UnknownError',
            message: error?.message ?? 'Phone verification failed.',
          });
        },
      });
    });
  };

  public signIn = async (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: this.cognitoUserPool,
      };

      const cognitoUser = new CognitoUser(userData);
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (session) => {
          console.log('✅ Login successful:', session);

          this.currentUser = cognitoUser; // ✅ Store the authenticated user instance

          // ✅ Start the session refresh interval
          this.startSessionRefresh();

          // ✅ Store user data
          const payload = session.getIdToken().payload;
          const emailVerified: boolean = payload?.['email_verified'];
          const phoneNumberVerified: boolean =
            payload?.['phone_number_verified'];

          const user: User = {
            sub: payload?.['sub'],
            email: payload?.['email'],
            emailVerified,
            phoneNumber: payload?.['phone_number'],
            phoneNumberVerified,
          };

          this.storageService.set('user', JSON.stringify(user));

          this.user.set(user);

          if (emailVerified && this.authData()?.hasCompletedSignup) {
            this.verification.set('complete');
          } else if (!emailVerified) {
            this.verification.set('email');
          } else {
            this.verification.set('phone');
          }

          // ✅ Extract and store tokens
          const idToken = session.getIdToken().getJwtToken();
          const accessToken = session.getAccessToken().getJwtToken();
          const refreshToken = session.getRefreshToken().getToken();

          this.saveTokens(idToken, accessToken, refreshToken);
          this.isLoggedIn.set(true);

          this.userData = await this.loadCurrentUser() as UserProfile;
          await this.reloadAuthData();
          resolve();
        },
        onFailure: (err) => {
          reject({
            code: err?.code ?? 'UnknownError',
            message: err?.message ?? 'Login failed.',
          });
        },
      });
    });
  };

  public startSessionRefresh(): void {
    // ✅ Prevent multiple intervals
    if (this.sessionRefreshInterval) {
      clearInterval(this.sessionRefreshInterval);
    }

    if (!this.isBrowser) {
      console.log('not starting interval');
      return;
    }
    this.sessionRefreshInterval = setInterval(() => {
      if (this.isLoggedIn()) {
        this.refreshSession().catch((err) =>
          console.error('🔴 Session refresh failed:', err)
        );
      }
    }, 50 * 60 * 1000); // Refresh every 50 minutes

    console.log('✅ started refresh token intervals');
  }

  public saveTokens(
    idToken: string,
    accessToken: string,
    refreshToken: string
  ): void {
    const cookieOptions: CookieOptions = {
      expires: 1, // Expires in 1 day
      path: '/',
      secure: true, // Ensures HTTPS only
      sameSite: 'Strict', // Protects against CSRF
      partitioned: false, // Not needed in most cases
    };

    this.storageService.set('id_token', idToken, cookieOptions);
    this.storageService.set('access_token', accessToken, cookieOptions);
    this.storageService.set('refresh_token', refreshToken, {
      ...cookieOptions,
      expires: 30, // Refresh token lasts longer (30 days)
    });

    console.log('✅ Tokens saved securely in cookies.');
  }

  public getAccessToken(): string | null {
    return this.storageService.get('access_token') ?? null;
  }

  public getIdToken(): string | null {
    return this.storageService.get('id_token') ?? null;
  }
  public getRefreshToken(): string | null {
    return this.storageService.get('refresh_token') ?? null;
  }

  public clearTokens(): void {
    this.storageService.delete('id_token', '/');
    this.storageService.delete('access_token', '/');
    this.storageService.delete('refresh_token', '/');
    this.storageService.delete('user', '/');
    this.storageService.delete('authStatus', '/');
  }

  public signOut(): void {
    this.clearTokens();
    this.isLoggedIn.set(false);
    this.user.set(undefined);
    this.currentUser = null;
    this.verification.set('initial');
    this.authData.set(undefined);
    this.userData = undefined;
    this.shared.signOutListener.next(true);
    // ✅ Stop session refresh
    if (this.sessionRefreshInterval) {
      clearInterval(this.sessionRefreshInterval);
      this.sessionRefreshInterval = null;
    }
    

    console.log('🔴 User logged out, tokens cleared.');
  }

  public refreshSession = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      this.currentUser = this.getUserData();
      if (!this.currentUser) {
        reject({
          code: 'NotAuthenticated',
          message: 'User is not authenticated.',
        });
        this.clearTokens();
        return;
      }

      const refreshToken = new CognitoRefreshToken({
        RefreshToken: this.getRefreshToken() ?? '',
      });

      // ✅ Refresh the session using the Refresh Token
      // @ts-ignore
      this.currentUser.refreshSession(refreshToken, (err, newSession) => {
        if (err) {
          console.error('Session refresh failed:', err);
          this.isLoggedIn.set(false);
          this.clearTokens();
          reject({
            code: err?.code ?? 'RefreshFailed',
            message:
              err?.message ?? 'Session refresh failed. Please log in again.',
          });
          this.notificationService.showCustomMessage(
            '',
            'Session expired, please sign back in.'
          );
          this.router.navigateByUrl(`/${this.langService.lang()}/login`);
          return;
        }

        console.log('✅ Session refreshed:', newSession);

        // ✅ Store new tokens
        const idToken = newSession.getIdToken().getJwtToken();
        const accessToken = newSession.getAccessToken().getJwtToken();
        const refreshToken = newSession.getRefreshToken().getToken();

        this.saveTokens(idToken, accessToken, refreshToken);
        this.reloadAuthData();
        this.isLoggedIn.set(true);
        resolve();
      });
    });
  };

  public isTokenOlderThanMinutes(token: string, minutes: number): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const iat = payload.iat; // Issued at timestamp (seconds)
      if (!iat) return true;

      const currentTime = Math.floor(Date.now() / 1000);
      return currentTime - iat > minutes * 60; // Check if token is older than X minutes
    } catch (error) {
      console.log('🔴 Failed to decode token:', error);
      return true;
    }
  }
  
  public async reloadAuthData(callback?: Function, forceReload = false) {
    const currentLang = this.translate.language;
    let regionCode: string | undefined;
    
    if (currentLang) {
      try {
        regionCode =
          languageRegionCodesMap[currentLang as LanguageRegionMap];
      } catch {
        regionCode = 'en-en';
      }
    }
    
    try {
      const token = this.storageService.get('access_token');
      if (!this.authData() || forceReload) {
        // ✅ Waits for `authStatus` to return data
        const data = await this.clientService.authStatus({
          headers: {
            'Accept-Language': currentLang,
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        this.authData.set(data);
        if (data) {
          this.userData = await this.loadCurrentUser() as UserProfile;
        } else {
          console.log('🔴 No auth status returned');
        }
      }

      this.verification.set(this.verification());

    } catch (error) {
      console.log('🔴 Error fetching auth status on sign in', error);
    }
    
  }

  public verifyEmail(token: string) {
    return new Promise((resolve, reject) => {
      this.http.post(`${environment.api}/public/v1/verify-email`, {
        token,
      }, {
        headers: {
          'Accept-Language': this.translate.language,
          'Content-Type': 'application/json',
        },
      }).subscribe({
        next(response) {
          resolve(response);
        },
        error(err) {
          reject(err);
        }
      });
    });
  }

  public resendEmailVerification() {
    const token = this.storageService.get('access_token');
    const request = this.http.post(`${environment.api}/api/v1/users/me/resend-verification`, {
      clientDomain: this.clientService.hostName,
    }, {
      headers: {
        'Accept-Language': this.translate.language,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    })

    return request;
  }

  public resendEmailVerificationCode() {
    const token = this.storageService.get('access_token');
    const phoneNumber = this.userData?.phoneNumber;
    const request = this.http.post(`${environment.api}/api/v1/users/me/email-verification/send-code`, {
      
    }, {
      headers: {
        'Accept-Language': this.translate.language,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    })
    return request;
  }

  public verifyEmailCode(code:string) {
    const token = this.storageService.get('access_token');
    const request = this.http.post(`${environment.api}/api/v1/users/me/email-verification/verify`, {
      code,
    }, {
      headers: {
        'Accept-Language': this.translate.language,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    })
    return request;
  }

  public resendPhoneVerification() {
    const token = this.storageService.get('access_token');
    const phoneNumber = this.userData?.phoneNumber;
    const request = this.http.post(`${environment.api}/api/v1/users/me/phone-verification/send-code`, {
      phoneNumber,
    }, {
      headers: {
        'Accept-Language': this.translate.language,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    })
    return request;
  }

  public verifyPhone(code:string) {
    const token = this.storageService.get('access_token');
    const request = this.http.post(`${environment.api}/api/v1/users/me/phone-verification/verify`, {
      code,
    }, {
      headers: {
        'Accept-Language': this.translate.language,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    })
    return request;
  }

  public verifyProcessPending() {
    const signedIn = this.isLoggedIn();
    if (signedIn) {
      return false;
    }
    const verificationStatus = this.verification();
    const notVerified = 'phone|email'.indexOf(verificationStatus) !== -1;
    const data = this.authData();
    let pending = false;

    if (signedIn) {
      if (data?.hasAccess) {
        return false
      }
      if (notVerified) {
        pending = true;
      }
      if (!data?.hasCompletedSignup || !data?.hasRequestedAccessToClient) {
        pending = true;
      }
      if (data?.userStatus === 'PENDING' || data?.userStatus === 'REJECTED' ) {
        pending = true;
      }
    }
    return pending;
  }

  public async initialize() {
    return new Promise(async (resolve, reject) => {
      if (this.isBrowser) {
        const idToken = this.getIdToken();
        if (idToken) {
          if (this.isTokenOlderThanMinutes(idToken, 5)) {
            console.log(
              '🔄 ID Token is older than 5 minutes, attempting refresh...'
            );
            try {
              await this.refreshSession();
              console.log('✅ Token successfully refreshed on page load');
              this.isLoggedIn.set(true);
              this.startSessionRefresh();
            } catch (error:any){
              console.error('🔴 Token refresh failed:', error);
            };
            
          } else {
            console.log('✅ Session is valid on reload');
            this.isLoggedIn.set(true);
            this.startSessionRefresh();
          }

          await this.reloadAuthData();

        }
      }
      resolve(true);
    });
  }

  dashboardRedirect({token, site, id_token, access_token}: {token: string, site: string, id_token: string, access_token: string}) {
    return new Promise((resolve, reject) => {
      this.http.post(`/dashboard_redirect`, {token, site, id_token, access_token}).subscribe({
        next: (response: any) => {
          resolve(response);
        },
        error: (error) => {
          reject('Unable to get redirect url');
        }
      });
    });
  }

  loadCurrentUser() {
    return new Promise(async (resolve, reject) => {
      const currentLang = this.translate.language;
      const token = this.storageService.get('access_token');
      if (token) {
        const request = this.http.get(environment.api + '/api/v1/users/current', {
          headers: {
            'Accept-Language': currentLang,
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
        try { 
          const response = await firstValueFrom(request);
          const data = response as UserProfile;
          resolve(data);
        } catch(err) {
          resolve(undefined);
        }
      } else {
        resolve(undefined);
      }
    });
  }

  load() {
    return new Promise(async (resolve, reject) => {
      await this.reloadAuthData();
      resolve(true);
    });
  }

}
